import { reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import type {
  Group,
  GroupItem,
  GroupItemAlterCode,
  GroupItemCustomer,
  GroupItemDetail,
  GroupItemMaterial,
  GroupItemTraderSite,
} from '@/types/Groups';
import { RecordMode, type GroupCategory, type TableFilterData } from '@/types/Base';
import type { TreeType } from '@/types/ComponentTypes';
import {
  getGroupItemMaterialForGroupItemId,
  getGroupItemsAlterCodeForGroupItemId,
  getGroupItemsCustomerForGroupItemId,
  getGroupItemsTraderSiteForGroupItemId,
} from '@/services/api/groups';
import { filterData, sortData } from '@/utils/table';

export const useGroupingStore = defineStore('grouping', () => {
  const category = ref<GroupCategory>('');
  const mode = ref<'edit' | 'view'>('view');

  const groups = ref<Group[]>([]);
  const modifiedGroups = ref<Group[]>([]);
  const selectedGroup = ref<Group>(initializeGroup());
  const groupModes = reactive(new Map<string, 'edit' | 'view'>());

  const groupItems = ref<GroupItem[]>([]);
  const selectedGroupItem = ref<GroupItem>();

  const groupItemNodes = ref<TreeType[]>([]);
  const stateGroupItemsNodes = ref<TreeType[]>([]);

  const groupItemsMaterial = ref<GroupItemMaterial[]>([]);
  const groupItemsMaterialTotalCount = ref<number>();

  const groupItemsAlterCode = ref<GroupItemAlterCode[]>([]);
  const groupItemsAlterCodeTotalCount = ref<number>();

  const groupItemsCustomer = ref<GroupItemCustomer[]>([]);
  const groupItemsCustomerTotalCount = ref<number>();

  const groupItemsTraderSite = ref<GroupItemTraderSite[]>([]);
  const groupItemsTraderSiteTotalCount = ref<number>();

  const treeNodesLimit = ref(100);

  function resetSelectedGroup() {
    selectedGroup.value = initializeGroup();
  }

  function initializeGroup(): Group {
    const groupId = uuidv4();
    return {
      id: groupId,
      isActive: true,
      isFromManager: true,
      name: '',
      maxLevel: 0,
      rootCode: '1',
      isSystem: false,
      groupItems: [],
      recordMode: RecordMode.Added,
    };
  }

  function resetGroupModesToView() {
    groupModes.forEach((_, key) => {
      groupModes.set(key, 'view');
    });
  }

  function clearGroupItemTables() {
    groupItemsAlterCode.value = [];
    groupItemsCustomer.value = [];
    groupItemsMaterial.value = [];
    groupItemsTraderSite.value = [];
  }

  function getModifiedGroupItems(
    selectedGroupItem: GroupItem,
    data?: TableFilterData
  ): GroupItemDetail[] {
    if (!selectedGroupItem) return [];
    const modifiedGroup = modifiedGroups.value.find(
      (group: Group) => group.id === selectedGroupItem?.groupId
    );

    if (!modifiedGroup?.groupItems) return [];
    const modifiedGroupItem = modifiedGroup.groupItems.find(
      (item) => item.id === selectedGroupItem?.id
    );
    if (!modifiedGroupItem) return [];

    const { groupItemMaterials, groupItemAlterCodes, groupItemCustomers, groupItemTraderSites } =
      modifiedGroupItem;

    let modifiedGroupItemDetails = [
      ...(groupItemMaterials || []),
      ...(groupItemAlterCodes || []),
      ...(groupItemCustomers || []),
      ...(groupItemTraderSites || []),
    ];

    // filter out deleted items to avoid duplication
    modifiedGroupItemDetails = modifiedGroupItemDetails.filter(
      (item) => item.recordMode !== RecordMode.Deleted
    );

    if (data?.query)
      modifiedGroupItemDetails = filterData(modifiedGroupItemDetails, data.query, ['name', 'code']);

    return modifiedGroupItemDetails;
  }

  function getDeletedGroupItems(selectedGroupItem: GroupItem): GroupItemDetail[] {
    if (!selectedGroupItem) return [];
    const modifiedGroup = modifiedGroups.value.find(
      (group: Group) => group.id === selectedGroupItem?.groupId
    );

    if (!modifiedGroup?.groupItems) return [];
    const modifiedGroupItem = modifiedGroup.groupItems.find(
      (item) => item.id === selectedGroupItem?.id
    );
    if (!modifiedGroupItem) return [];

    const { groupItemMaterials, groupItemAlterCodes, groupItemCustomers, groupItemTraderSites } =
      modifiedGroupItem;

    const allModifiedItems = [
      ...(groupItemMaterials || []),
      ...(groupItemAlterCodes || []),
      ...(groupItemCustomers || []),
      ...(groupItemTraderSites || []),
    ];

    // return only deleted items
    return allModifiedItems.filter((item) => item.recordMode === RecordMode.Deleted);
  }

  const groupItemsDetailsTotalCount = ref<number>();
  const groupItemsDetails = ref<GroupItemDetail[]>([]);
  const groupItemsDetailsSelectedPage = ref(1);
  let serverData: GroupItemDetail[] = [];
  let serverTotalCount = 0;

  function getHeadersForCategory(categoryValue: string) {
    switch (categoryValue) {
      case 'material':
        return [
          { value: 'material.name', text: 'Name', type: 'text', active: true },
          { value: 'material.code', text: 'Code', type: 'text', active: true },
          { value: 'deleted', text: 'Status', type: 'status', active: true },
          { value: 'material.deleted', text: 'Material Status', type: 'status', active: true },
          { value: 'actions', text: 'Actions', active: true },
        ];
      case 'alterCode':
        return [
          { value: 'alterCode.name', text: 'Name', type: 'text', active: true },
          { value: 'alterCode.code', text: 'Code', type: 'text', active: true },
          { value: 'deleted', text: 'Status', type: 'status', active: true },
          { value: 'alterCode.deleted', text: 'AlterCode Status', type: 'status', active: true },
          { value: 'actions', text: 'Actions', active: true },
        ];
      case 'customer':
        return [
          { value: 'customer.trader.name', text: 'Name', type: 'text', active: true },
          { value: 'customer.code', text: 'Code', type: 'text', active: true },
          { value: 'deleted', text: 'Status', type: 'status', active: true },
          { value: 'customer.deleted', text: 'Customer Status', type: 'status', active: true },
          { value: 'actions', text: 'Actions', active: true },
        ];
      case 'traderSite':
        return [
          { value: 'traderSite.name', text: 'Name', type: 'text', active: true },
          { value: 'traderSite.code', text: 'Code', type: 'text', active: true },
          { value: 'deleted', text: 'Status', type: 'status', active: true },
          { value: 'tradeSite.deleted', text: 'TraderSite Status', type: 'status', active: true },
          { value: 'actions', text: 'Actions', active: true },
        ];
      default:
        return [];
    }
  }

  async function getGroupItemsTableData(data: TableFilterData, addedItems?: boolean) {
    if (!selectedGroupItem.value) return;

    const currentPage = data.page ?? 1;
    const currentItemPerPage = data.itemsPerPage ?? 50;

    const modifiedGroupItemDetails = getModifiedGroupItems(selectedGroupItem.value, data);
    const hasModifiedItems = modifiedGroupItemDetails.length > 0;

    // Get deleted items
    const deletedItems = getDeletedGroupItems(selectedGroupItem.value);

    const headers = getHeadersForCategory(category.value);
    if (!addedItems) {
      if (hasModifiedItems) {
        //sort locally when modified items
        switch (category.value) {
          case 'material':
            {
              const resMaterial = await getGroupItemMaterialForGroupItemId({
                page: 1,
                itemsPerPage: serverTotalCount,
                groupItemId: selectedGroupItem.value.id,
                query: data.query,
                sortColumn: '',
                sortAscending: true,
              });
              if (resMaterial) {
                serverData = resMaterial.data;
                serverTotalCount = resMaterial.count;
              }
            }
            break;
          case 'alterCode':
            {
              const resAlterCode = await getGroupItemsAlterCodeForGroupItemId({
                page: 1,
                itemsPerPage: serverTotalCount,
                groupItemId: selectedGroupItem.value.id,
                query: data.query,
                sortColumn: '',
                sortAscending: true,
              });
              if (resAlterCode) {
                serverData = resAlterCode.data;
                serverTotalCount = resAlterCode.count;
              }
            }
            break;

          case 'customer':
            {
              const resCustomer = await getGroupItemsCustomerForGroupItemId({
                page: 1,
                itemsPerPage: serverTotalCount,
                groupItemId: selectedGroupItem.value.id,
                query: data.query,
                sortColumn: '',
                sortAscending: true,
              });
              if (resCustomer) {
                serverData = resCustomer.data;
                serverTotalCount = resCustomer.count;
              }
            }
            break;

          case 'traderSite':
            {
              const resTraderSite = await getGroupItemsTraderSiteForGroupItemId({
                page: 1,
                itemsPerPage: serverTotalCount,
                groupItemId: selectedGroupItem.value.id,
                query: data.query,
                sortColumn: '',
                sortAscending: true,
              });
              if (resTraderSite) {
                serverData = resTraderSite.data;
                serverTotalCount = resTraderSite.count;
              }
            }
            break;

          default:
            console.error(`Unknown category: ${category.value}`);
            break;
        }
      } else {
        switch (category.value) {
          case 'material':
            {
              const resMaterial = await getGroupItemMaterialForGroupItemId({
                page: currentPage,
                itemsPerPage: currentItemPerPage,
                groupItemId: selectedGroupItem.value.id,
                query: data.query,
                sortColumn: data.sortColumn,
                sortAscending: data.sortAscending,
              });
              if (resMaterial) {
                serverData = resMaterial.data;
                serverTotalCount = resMaterial.count;
              }
            }
            break;
          case 'alterCode':
            {
              const resAlterCode = await getGroupItemsAlterCodeForGroupItemId({
                page: currentPage,
                itemsPerPage: currentItemPerPage,
                groupItemId: selectedGroupItem.value.id,
                query: data.query,
                sortColumn: data.sortColumn,
                sortAscending: data.sortAscending,
              });
              if (resAlterCode) {
                serverData = resAlterCode.data;
                serverTotalCount = resAlterCode.count;
              }
            }
            break;

          case 'customer':
            {
              const resCustomer = await getGroupItemsCustomerForGroupItemId({
                page: currentPage,
                itemsPerPage: currentItemPerPage,
                groupItemId: selectedGroupItem.value.id,
                query: data.query,
                sortColumn: data.sortColumn,
                sortAscending: data.sortAscending,
              });
              if (resCustomer) {
                serverData = resCustomer.data;
                serverTotalCount = resCustomer.count;
              }
            }
            break;

          case 'traderSite':
            {
              const resTraderSite = await getGroupItemsTraderSiteForGroupItemId({
                page: currentPage,
                itemsPerPage: currentItemPerPage,
                groupItemId: selectedGroupItem.value.id,
                query: data.query,
                sortColumn: data.sortColumn,
                sortAscending: data.sortAscending,
              });
              if (resTraderSite) {
                serverData = resTraderSite.data;
                serverTotalCount = resTraderSite.count;
              }
            }
            break;

          default:
            console.error(`Unknown category: ${category.value}`);
            break;
        }
      }
    }
    // Helper function to preserve deleted status from locally modified items
    function preserveDeletedStatus(items: GroupItemDetail[]): GroupItemDetail[] {
      return items.map((item) => {
        const deletedItem = deletedItems.find(
          (modItem) => modItem.id === item.id && modItem.recordMode === RecordMode.Deleted
        );

        if (deletedItem) {
          return { ...item, deleted: true, recordMode: RecordMode.Deleted };
        }

        return item;
      });
    }

    if (hasModifiedItems) {
      // Combine server data with modified data for unified sorting
      const serverDataWithDeletedStatus = preserveDeletedStatus(serverData);
      const allItems = [...serverDataWithDeletedStatus, ...modifiedGroupItemDetails];

      // Apply sorting to the combined dataset
      let sortedItems = allItems;
      if (data?.sortColumn && data.sortColumn.length > 0) {
        const columnIndex = headers.findIndex((header) => header.value === data.sortColumn);
        if (columnIndex !== -1) {
          const sortOrder = data.sortAscending ? 'asc' : 'desc';
          sortedItems = sortData(allItems, columnIndex, sortOrder, headers);
        }
      }

      const startIndex = (currentPage - 1) * currentItemPerPage;
      const endIndex = startIndex + currentItemPerPage;

      groupItemsDetails.value = sortedItems.slice(startIndex, endIndex);
      groupItemsDetailsTotalCount.value = sortedItems.length;
    } else {
      // Use server data directly when no modified items
      const serverDataWithDeletedStatus = preserveDeletedStatus(serverData);
      groupItemsDetails.value = serverDataWithDeletedStatus;
      groupItemsDetailsTotalCount.value = serverTotalCount;
    }
    if (addedItems)
      groupItemsDetailsSelectedPage.value = Math.ceil(
        groupItemsDetailsTotalCount.value / currentItemPerPage
      );
  }

  return {
    groups,
    mode,
    modifiedGroups,
    selectedGroup,
    groupItems,
    groupItemNodes,
    stateGroupItemsNodes,
    initializeGroup,
    category,
    groupModes,
    resetGroupModesToView,
    selectedGroupItem,
    groupItemsMaterial,
    groupItemsMaterialTotalCount,
    groupItemsAlterCode,
    groupItemsAlterCodeTotalCount,
    groupItemsCustomer,
    groupItemsCustomerTotalCount,
    groupItemsTraderSite,
    groupItemsTraderSiteTotalCount,
    clearGroupItemTables,
    resetSelectedGroup,
    treeNodesLimit,
    getGroupItemsTableData,
    groupItemsDetails,
    groupItemsDetailsTotalCount,
    groupItemsDetailsSelectedPage,
  };
});
